import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { formatContent } from '../utils/openaiService';

const SAMPLE_CONTENT = `Academic Excellence Scholarship (International)
Contact us
Key facts
Entry	For courses starting in September 2025 and January 2026. Scholarships cannot be deferred.
Eligibility	
Any overseas applicant for either a full-time taught or part-time structured postgraduate course delivered on campus or Master's by research delivered on campus
You must have achieved a minimum of an upper second-class Honours degree (or international equivalent grades - see below), or a CPE with a merit profile
Exclusions	
If you are applying for a Postgraduate Diploma, distance learning course or certain professional development and Postgraduate Diploma courses taught by the Faculty of Health & Social Sciences, you will not be eligible for this award
Eligible students on MSc Public Health, MA Social Work, MSc Physician Associate Studies and MSc Nutrition & Behaviour will receive the award
You can receive this scholarship alongside a Sports or Music scholarship, but cannot receive any other academic award
Part-time, student-driven and distance learning (i.e online courses) are not eligible
Value	
 £4,500

Number available	Unlimited
Need to fill in an application	No - you will automatically be considered for this award if you meet the criteria*
The Postgraduate Academic Excellence Scholarship for international students is designed to recognise outstanding academic achievement.

Please check out our 2025/26 equivalences table (330.42kb) for September 2025 and January 2026 entry to find out more about the qualifications we accept from your country.

If your country isn't listed there, please contact our International Admissions Team, who will assess your qualifications.

*If you think you may be eligible for this award but do not receive a confirmation when you enrol at BU, please contact AskBU, who will confirm your eligibility on review of original qualification transcripts.

Important information
Scholarships are reviewed on a regular basis and the university reserves the right to withdraw, amend or introduce new scholarships at any time. Scholarships are offered subject to funding and availability. Scholarship is awarded in your first year only, unless stated otherwise.

For full Terms and Conditions, please view our Scholarship Policy.`;

// Custom components for ReactMarkdown to control heading sizes and spacing
const customComponents = {
  h1: ({ children }) => (
    <Typography variant="h5" component="h5" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="h6" component="h6" sx={{ fontWeight: 'bold', mb: 1, mt: 1.5 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle1" component="h6" sx={{ fontWeight: 'bold', mb: 0.5, mt: 1 }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body1" component="p" sx={{ mb: 1.5, lineHeight: 1.6 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 2, mb: 1.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2, mb: 1.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
      {children}
    </Typography>
  )
};

const MainFormatter = ({ settings }) => {
  const [content, setContent] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [viewMode, setViewMode] = useState('raw');

  // Load content and output from localStorage on component mount
  useEffect(() => {
    const savedContent = localStorage.getItem('markdownFormatterContent');
    const savedOutput = localStorage.getItem('markdownFormatterOutput');
    const savedViewMode = localStorage.getItem('markdownFormatterViewMode');
    
    if (savedContent) {
      setContent(savedContent);
    }
    if (savedOutput) {
      setOutput(savedOutput);
    }
    if (savedViewMode) {
      setViewMode(savedViewMode);
    }
  }, []);

  // Save content to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('markdownFormatterContent', content);
  }, [content]);

  // Save output to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('markdownFormatterOutput', output);
  }, [output]);

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('markdownFormatterViewMode', viewMode);
  }, [viewMode]);

  const handleFormatContent = async () => {
    if (!content.trim()) {
      showNotification('Please enter content to format', 'warning');
      return;
    }

    if (!settings.prompt) {
      showNotification('No formatting prompt found. Please check Settings', 'error');
      return;
    }

    setLoading(true);
    try {
      const formattedContent = await formatContent(content, settings.prompt, settings);
      setOutput(formattedContent);
      showNotification('Content formatted successfully', 'success');
    } catch (error) {
      console.error('Error formatting content:', error);
      showNotification(
        `Error: ${error.message || 'Failed to format content. Please check your API settings.'}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyOutput = () => {
    if (!output) {
      showNotification('No content to copy', 'warning');
      return;
    }
    
    navigator.clipboard.writeText(output)
      .then(() => showNotification('Markdown copied to clipboard', 'success'))
      .catch(() => showNotification('Failed to copy to clipboard', 'error'));
  };

  const handleClearContent = () => {
    setContent('');
  };

  const handleClearOutput = () => {
    setOutput('');
  };

  const handleSampleInput = () => {
    setContent(SAMPLE_CONTENT);
    showNotification('Sample content loaded', 'success');
  };

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleOutputChange = (e) => {
    setOutput(e.target.value);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 200px)' }}>
      <Grid container spacing={2} sx={{ height: '100%' }}>
        <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Content Input
          </Typography>
          <TextField
            label="Paste scholarship content here"
            multiline
            value={content}
            onChange={(e) => setContent(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{ 
              flexGrow: 1,
              mb: 1,
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start'
              },
              '& .MuiInputBase-input': {
                height: '100% !important',
                overflow: 'auto !important'
              }
            }}
            InputProps={{
              style: { height: '100%' }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearContent}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PlaylistAddIcon />}
              onClick={handleSampleInput}
            >
              Sample Input
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <FormatAlignLeftIcon />}
              onClick={handleFormatContent}
              disabled={loading || !content.trim()}
            >
              {loading ? 'Formatting...' : 'Format Content'}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              Markdown Output
            </Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              aria-label="view mode"
            >
              <ToggleButton value="raw" aria-label="raw markdown">
                <CodeIcon fontSize="small" />
                <Typography variant="body2" sx={{ ml: 0.5 }}>Raw</Typography>
              </ToggleButton>
              <ToggleButton value="preview" aria-label="rendered preview" disabled={!output}>
                <VisibilityIcon fontSize="small" />
                <Typography variant="body2" sx={{ ml: 0.5 }}>Preview</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {viewMode === 'raw' ? (
            <TextField
              label="Edit your markdown here"
              multiline
              value={output}
              onChange={handleOutputChange}
              fullWidth
              variant="outlined"
              sx={{ 
                flexGrow: 1,
                mb: 1,
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'flex-start'
                },
                '& .MuiInputBase-input': {
                  height: '100% !important',
                  overflow: 'auto !important',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem'
                }
              }}
              InputProps={{
                style: { height: '100%' }
              }}
            />
          ) : (
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                flexGrow: 1,
                overflow: 'auto',
                backgroundColor: '#ffffff',
                mb: 1
              }}
              className="markdown-output"
            >
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]} 
                rehypePlugins={[rehypeSanitize]}
                components={customComponents}
              >
                {output}
              </ReactMarkdown>
            </Paper>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearOutput}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyOutput}
              disabled={!output}
            >
              Copy to Clipboard
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainFormatter; 